import * as yup from 'yup';
import { useTranslation } from 'react-i18next';
import { Divider, Grid, Tab, Tabs, Typography } from '@mui/material';
import React, { useState } from 'react';
import { Field, FieldProps, Form, Formik, FormikHelpers } from 'formik';
import { usePostHog } from 'posthog-js/react';
import { KeyedMutator } from 'swr/_internal';
import { AccountData, AccountService, Language, PartialLabelData, PassOrderPageData } from '../../../api';
import { InformationSectionLanguageTab } from '../../../experienceSettings/sections/informationSection/InformationSectionLanguageTab';
import { UseAuthPermissions } from '../../../session/UseAuthPermissions';
import { dynamicLabelSchema } from '../../../experienceSettings/sections/informationSection/dynamicLabelSchema';
import { FormRichTextField } from '../../../experienceSettings/formComponents/FormRichTextField';
import { PartialLabelListDrawer } from '../../../experienceSettings/sections/informationSection/PartialLabelListDrawer';
import { SectionSaveRegistrar } from '../../../experienceSettings/saveHeader/SectionSaveRegistrar';
import type { ChangeEntry } from '../../../experienceSettings/saveHeader/SaveHeaderContext';
import {
  htmlValuesDiffer,
  renderRichDiffPair,
  renderTextDiffPair,
} from '../../../experienceSettings/saveHeader/richTextDiff';
import { PassSettingsPageTabs } from '../PassSettingsPageTabs';

type PassInformationSectionProps = {
  passOrderPage: PassOrderPageData;
  languages: Language[];
  updateLocalAccount: KeyedMutator<AccountData>;
};

type PassInformationFormValues = {
  label: { [key in Language]?: string };
  description: { [key in Language]?: string } | undefined;
  disclaimers?: PartialLabelData;
  order_page_items_descriptions?: PartialLabelData[];
  practical_info_descriptions?: PartialLabelData[];
};

export const PassInformationSection = ({
  passOrderPage,
  languages,
  updateLocalAccount,
}: PassInformationSectionProps) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState(languages[0]);
  const { readOnly } = UseAuthPermissions();
  const posthog = usePostHog();

  const handleTabChange = (event: React.SyntheticEvent, newValue: Language) => {
    setActiveTab(newValue);
  };

  const validationSchema = yup.object().shape({
    label: yup.object().shape(dynamicLabelSchema(t, languages, true)),
    description: yup.object().shape(dynamicLabelSchema(t, languages)).nullable(),
  });

  const initialValues: PassInformationFormValues = {
    label: passOrderPage.label,
    description: passOrderPage.description,
    disclaimers: passOrderPage.disclaimers,
    order_page_items_descriptions: passOrderPage.order_page_items_descriptions || [],
    practical_info_descriptions: passOrderPage.practical_info_descriptions || [],
  };

  const handleSubmit = async (
    values: PassInformationFormValues,
    { setStatus }: FormikHelpers<PassInformationFormValues>,
  ) => {
    try {
      posthog.capture('Editing pass settings', { section: 'informationSection', passOrderPageId: passOrderPage.id });
      const updatedAccount = await AccountService.updatePassOrderPage({
        id: passOrderPage.id,
        label: values.label,
        description: values.description,
        disclaimers: values.disclaimers,
        order_page_items_descriptions: values.order_page_items_descriptions,
        practical_info_descriptions: values.practical_info_descriptions,
        num_punches: passOrderPage.num_punches,
      });
      await updateLocalAccount(updatedAccount);
    } catch (e) {
      setStatus({ error: (e as Error)?.message || t('saveHeader.errorGeneric') });
      throw e;
    }
  };

  const getChanges = (values: PassInformationFormValues, iv: PassInformationFormValues): ChangeEntry[] => {
    const changes: ChangeEntry[] = [];
    languages.forEach((lang) => {
      if (values.label?.[lang] !== iv.label?.[lang]) {
        changes.push({
          label: `${t('saveHeader.name')} (${t(`emailTemplates.${lang}`)})`,
          ...renderTextDiffPair(iv.label?.[lang], values.label?.[lang]),
        });
      }
      if (htmlValuesDiffer(values.description?.[lang], iv.description?.[lang])) {
        changes.push({
          label: `${t('editOrderPage.orderPagePassDescription')} (${t(`emailTemplates.${lang}`)})`,
          ...renderRichDiffPair(iv.description?.[lang], values.description?.[lang]),
        });
      }
      if (htmlValuesDiffer(values.disclaimers?.[lang], iv.disclaimers?.[lang])) {
        changes.push({
          label: `${t('editOrderPage.disclaimers')} (${t(`emailTemplates.${lang}`)})`,
          ...renderRichDiffPair(iv.disclaimers?.[lang], values.disclaimers?.[lang]),
        });
      }
    });
    return changes;
  };

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}>
      {(formik) => {
        const { isSubmitting } = formik;
        const formIsDisabled = readOnly || isSubmitting;
        return (
          <Form onSubmit={formik.handleSubmit}>
            <SectionSaveRegistrar
              tabKey={PassSettingsPageTabs.INFORMATION}
              getChanges={getChanges}
              disabled={readOnly}
            />
            <Grid container justifyContent="flex-start">
              {languages.length > 1 && (
                <Tabs value={activeTab} onChange={handleTabChange} aria-label="language tabs">
                  {languages.map((lang) => (
                    <Tab key={lang} label={t(`emailTemplates.${lang}`)} value={lang} />
                  ))}
                </Tabs>
              )}
            </Grid>
            <Grid container mt={2}>
              {languages.map((lang) => (
                <Grid key={lang} hidden={activeTab !== lang}>
                  {activeTab === lang && <InformationSectionLanguageTab language={lang} disabled={formIsDisabled} />}
                </Grid>
              ))}
            </Grid>

            <Divider sx={{ mt: 4, mb: 2 }} />

            <Grid container flexDirection="column" gap={2}>
              <Grid item>
                <Typography variant="body2" color="text.secondary" mb={2}>
                  {t('editOrderPage.minisiteDetailsSubtitle')}
                </Typography>
              </Grid>
              <Grid item mb={3}>
                <Typography variant="subtitle1" fontWeight="600" mb={1}>
                  {t('editOrderPage.orderPagePassDescription')}
                </Typography>
                <FormRichTextField labelFieldName="description" disabled={formIsDisabled} />
              </Grid>

              <Grid item mb={3}>
                <Typography variant="subtitle1" fontWeight="600" mb={1}>
                  {t('editOrderPage.disclaimers')}
                </Typography>
                <Typography variant="body2" color="text.secondary" mb={1}>
                  {t('editOrderPage.disclaimersCaption')}
                </Typography>
                <FormRichTextField labelFieldName="disclaimers" disabled={formIsDisabled} />
              </Grid>

              <Field name="order_page_items_descriptions">
                {({ field, form }: FieldProps<PartialLabelData[]>) => (
                  <PartialLabelListDrawer
                    items={field.value || []}
                    onChange={(items) => form.setFieldValue(field.name, items)}
                    title={t('editOrderPage.whatsIncluded')}
                    subtitle={t('editOrderPage.whatsIncludedSubtitle')}
                    addButtonText={t('editOrderPage.addWhatsIncludedItem')}
                    emptyText={t('editOrderPage.noWhatsIncludedItems')}
                    dialogTitle={t('editOrderPage.whatsIncludedItem')}
                    fieldLabel={t('editOrderPage.itemText')}
                    disabled={formIsDisabled}
                  />
                )}
              </Field>

              <Field name="practical_info_descriptions">
                {({ field, form }: FieldProps<PartialLabelData[]>) => (
                  <PartialLabelListDrawer
                    items={field.value || []}
                    onChange={(items) => form.setFieldValue(field.name, items)}
                    title={t('editOrderPage.practicalInfo')}
                    subtitle={t('editOrderPage.practicalInfoSubtitle')}
                    addButtonText={t('editOrderPage.addPracticalInfoItem')}
                    emptyText={t('editOrderPage.noPracticalInfoItems')}
                    dialogTitle={t('editOrderPage.practicalInfoItem')}
                    fieldLabel={t('editOrderPage.itemText')}
                    disabled={formIsDisabled}
                  />
                )}
              </Field>
            </Grid>
          </Form>
        );
      }}
    </Formik>
  );
};
